import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import EventNotification from "../components/EventNotification";

function Home() {
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedLetter, setSelectedLetter] = useState(null);
	const [seniorManagement, setSeniorManagement] = useState([]);
	const [latestNews, setLatestNews] = useState([]);
	const [donorsPartners, setDonorsPartners] = useState([]);
	const [loadingManagement, setLoadingManagement] = useState(true);
	const [loadingNews, setLoadingNews] = useState(true);
	const [loadingPartners, setLoadingPartners] = useState(true);

	// Marquee: measure the rendered width so the CSS animation loops seamlessly on all screens.
	const marqueeSegmentRef = useRef(null);
	const [marqueeShiftPx, setMarqueeShiftPx] = useState(0);
	const navigate = useNavigate();

	const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
	const numbers = ["#"];

	const carouselItems = [
		//video item - starts the slider
		// {
		// 	type: "video",
		// 	src: "/videos/tgh2.mp4",
		// 	title: "Transforming your care",
			
		// 	link: "/overview",
		// 	buttonText: "Contact us",
		// },
		{
			type: "image",
			image: "/img/carousel/hidu.jpg",
			title: "Transforming your care",
			
			link: "/overview",
			buttonText: "Contact us",
		},
		{
			type: "image", // 'image' or 'video'
			image: "/img/carousel/vip.jpeg",
			title: "Delivering accessible healthcare to the population",
			// subtitle: "Learn how we drive innovation",
			link: "/about-us/overview",
			buttonText: "Contact us",
		},
		{
			type: "image",
			image: "/img/carousel/niccuu.jpg",
			title: "Your Health, Our Mission",
			subtitle:
				"Compassionate, patient-centered care with the latest medical advancements",
			link: "/general-care",
			buttonText: "Explore More",
		},
		{
			type: "image",
			image: "/img/carousel/obgyp.JPG",
			title: "Dedicated Healthcare Team",
			subtitle: "Empowering Health, Enriching Lives",
			link: "/specialized",
			buttonText: "Learn More",
		},
		// {
		// 	type: "image",
		// 	image: "/img/carousel/peripal.jpg",
		// 	title: "Dedicated Healthcare Team",
		// 	subtitle: "Empowering Health, Enriching Lives",
		// 	link: "/specialized",
		// 	buttonText: "Learn More",
		// },
	];

	// Fetch senior management
	useEffect(() => {
		const fetchManagement = async () => {
			try {
				const response = await axios.get("/api/senior-management");
				const management = response.data.management || [];
				setSeniorManagement(management);
			} catch (error) {
				console.error("Error fetching senior management:", error);
				setSeniorManagement([]);
			} finally {
				setLoadingManagement(false);
			}
		};
		fetchManagement();
	}, []);

	// Fetch latest news
	useEffect(() => {
		const fetchNews = async () => {
			try {
				const response = await axios.get("/api/news");
				// Get latest 3 news items
				const sortedNews = response.data.sort(
					(a, b) => new Date(b.date) - new Date(a.date),
				);
				setLatestNews(sortedNews.slice(0, 3));
			} catch (error) {
				console.error("Error fetching news:", error);
			} finally {
				setLoadingNews(false);
			}
		};
		fetchNews();
	}, []);

	// Fetch donors/partners
	useEffect(() => {
		const fetchPartners = async () => {
			try {
				const response = await axios.get("/api/donors-partners");
				setDonorsPartners(response.data.donorsPartners || []);
			} catch (error) {
				console.error("Error fetching donors/partners:", error);
			} finally {
				setLoadingPartners(false);
			}
		};
		fetchPartners();
	}, []);

	// Measure one full logo-row width (px). Must match the duplicated segment for a seamless loop.
	useLayoutEffect(() => {
		const el = marqueeSegmentRef.current;
		if (!el) return;

		let raf = 0;
		const update = () => {
			cancelAnimationFrame(raf);
			raf = requestAnimationFrame(() => {
				// scrollWidth is more reliable than getBoundingClientRect on mobile flex rows
				const w = Math.max(el.scrollWidth, el.getBoundingClientRect().width || 0);
				setMarqueeShiftPx(w > 0 ? w : 0);
			});
		};

		update();
		const t1 = window.setTimeout(update, 0);
		const t2 = window.setTimeout(update, 150);
		const t3 = window.setTimeout(update, 400);

		const onImg = () => update();
		const imgs = el.querySelectorAll("img");
		imgs.forEach((img) => {
			img.addEventListener("load", onImg);
			if (img.complete && img.naturalWidth > 0) onImg();
		});

		if (document.fonts?.ready) {
			document.fonts.ready.then(() => update());
		}

		let ro;
		if (typeof ResizeObserver !== "undefined") {
			ro = new ResizeObserver(() => update());
			ro.observe(el);
		} else {
			window.addEventListener("resize", update);
		}

		return () => {
			cancelAnimationFrame(raf);
			clearTimeout(t1);
			clearTimeout(t2);
			clearTimeout(t3);
			imgs.forEach((img) => img.removeEventListener("load", onImg));
			if (ro) ro.disconnect();
			else window.removeEventListener("resize", update);
		};
	}, [donorsPartners]);

	useEffect(() => {
		// Initialize hero carousel with autoplay
		const carouselElement = document.querySelector("#tgh-hero-carousel");
		if (carouselElement && window.bootstrap) {
			// Check if first slide is a video
			const initialActiveSlide = carouselElement.querySelector(
				".carousel-item.active",
			);
			const hasInitialVideo =
				initialActiveSlide && initialActiveSlide.querySelector("video");

			// Don't auto-start if first slide is a video - let video start the slider
			const carousel = new window.bootstrap.Carousel(carouselElement, {
				interval: 5000,
				ride: hasInitialVideo ? false : "carousel", // Don't auto-start if video is first
				pause: "hover", // Pause on hover for all slides
				wrap: true,
			});

			// Store carousel instance for later use
			window.tghCarousel = carousel;
			window.tghCarouselPaused = false; // Track if manually paused for video

			// Function to handle video end and resume carousel
			const handleVideoEnd = () => {
				if (window.tghCarousel && window.tghCarouselPaused) {
					window.tghCarouselPaused = false;
					// Only resume if not hovering
					if (!carouselElement.matches(":hover")) {
						window.tghCarousel.cycle();
					}
				} else if (window.tghCarousel && hasInitialVideo) {
					// If this was the initial video, start the carousel now
					if (!carouselElement.matches(":hover")) {
						window.tghCarousel.cycle();
					}
				}
			};

			// Function to pause carousel when video slide is active
			const pauseForVideo = (activeSlide) => {
				const video = activeSlide.querySelector("video");
				if (video) {
					// Pause carousel to wait for video to finish
					if (window.tghCarousel) {
						window.tghCarousel.pause();
						window.tghCarouselPaused = true; // Mark as paused for video
					}

					// Remove any existing event listeners
					video.removeEventListener("ended", handleVideoEnd);

					// Add event listener for when video ends
					video.addEventListener("ended", handleVideoEnd, { once: true });

					// Also listen for video errors to resume carousel
					const handleVideoError = () => {
						if (window.tghCarousel && window.tghCarouselPaused) {
							window.tghCarouselPaused = false;
							if (!carouselElement.matches(":hover")) {
								window.tghCarousel.cycle();
							}
						}
					};
					video.removeEventListener("error", handleVideoError);
					video.addEventListener("error", handleVideoError, { once: true });
				} else {
					// If no video, resume normal carousel cycling (if not paused for video)
					if (window.tghCarousel && !window.tghCarouselPaused) {
						// Only resume if not hovering
						if (!carouselElement.matches(":hover")) {
							window.tghCarousel.cycle();
						}
					}
				}
			};

			// Handle video playback on slide change
			const handleSlideChange = (event) => {
				// Pause all videos first
				const allVideos = carouselElement.querySelectorAll("video");
				allVideos.forEach((video) => {
					video.pause();
					video.removeEventListener("ended", handleVideoEnd);
				});

				// Play video in active slide
				const activeSlide = event.target.querySelector(".carousel-item.active");
				if (activeSlide) {
					const video = activeSlide.querySelector("video");
					if (video) {
						// Immediately pause carousel before video starts
						if (window.tghCarousel) {
							window.tghCarousel.pause();
							window.tghCarouselPaused = true;
						}

						video.currentTime = 0; // Reset to start

						// Set up video end handler BEFORE playing
						video.removeEventListener("ended", handleVideoEnd);
						video.addEventListener("ended", handleVideoEnd, { once: true });

						// Also listen for video errors
						const handleVideoError = () => {
							if (window.tghCarousel && window.tghCarouselPaused) {
								window.tghCarouselPaused = false;
								if (!carouselElement.matches(":hover")) {
									window.tghCarousel.cycle();
								}
							}
						};
						video.removeEventListener("error", handleVideoError);
						video.addEventListener("error", handleVideoError, { once: true });

						// Now play the video
						video.play().catch(() => {
							// If video fails to play, resume carousel
							if (window.tghCarousel && window.tghCarouselPaused) {
								window.tghCarouselPaused = false;
								if (!carouselElement.matches(":hover")) {
									window.tghCarousel.cycle();
								}
							}
						});
					} else {
						// No video in this slide, reset pause flag and resume normal cycling
						window.tghCarouselPaused = false;
						if (window.tghCarousel) {
							// Only resume if not hovering
							if (!carouselElement.matches(":hover")) {
								window.tghCarousel.cycle();
							}
						}
					}
				}
			};

			carouselElement.addEventListener("slid.bs.carousel", handleSlideChange);

			// Add hover pause functionality for all slides
			const handleMouseEnter = () => {
				if (window.tghCarousel) {
					window.tghCarousel.pause();
				}
			};

			const handleMouseLeave = () => {
				// Only resume if not paused for video
				if (window.tghCarousel && !window.tghCarouselPaused) {
					window.tghCarousel.cycle();
				}
			};

			carouselElement.addEventListener("mouseenter", handleMouseEnter);
			carouselElement.addEventListener("mouseleave", handleMouseLeave);

			// Handle initial slide
			if (initialActiveSlide) {
				const video = initialActiveSlide.querySelector("video");
				if (video) {
					// Set up video end handler to start carousel after video finishes
					video.removeEventListener("ended", handleVideoEnd);
					video.addEventListener("ended", handleVideoEnd, { once: true });

					// Also listen for video errors to start carousel
					const handleInitialVideoError = () => {
						if (window.tghCarousel) {
							if (!carouselElement.matches(":hover")) {
								window.tghCarousel.cycle();
							}
						}
					};
					video.addEventListener("error", handleInitialVideoError, {
						once: true,
					});

					// Play the video - this will start the slider after it finishes
					video.play().catch(() => {
						// If video fails to play, start normal cycling
						if (window.tghCarousel) {
							if (!carouselElement.matches(":hover")) {
								window.tghCarousel.cycle();
							}
						}
					});

					// Mark as paused for video so carousel doesn't start until video ends
					window.tghCarouselPaused = true;
				} else {
					// No video in initial slide, start normal cycling
					if (!carouselElement.matches(":hover")) {
						carousel.cycle();
					}
				}
			} else {
				// Start normal cycling if no active slide found
				if (!carouselElement.matches(":hover")) {
					carousel.cycle();
				}
			}

			// Cleanup function
			return () => {
				// Remove event listeners
				carouselElement.removeEventListener(
					"slid.bs.carousel",
					handleSlideChange,
				);
				carouselElement.removeEventListener("mouseenter", handleMouseEnter);
				carouselElement.removeEventListener("mouseleave", handleMouseLeave);

				// Remove global carousel reference on unmount
				if (window.tghCarousel) {
					delete window.tghCarousel;
				}
				if (window.tghCarouselPaused !== undefined) {
					delete window.tghCarouselPaused;
				}

				// Remove all video event listeners
				const allVideos = carouselElement.querySelectorAll("video");
				allVideos.forEach((video) => {
					video.removeEventListener("ended", handleVideoEnd);
				});
			};
		}

	}, []);

	const handleLetterClick = (letter) => {
		setSelectedLetter(letter);
		navigate(`/diseases/${letter}`);
		window.scrollTo(0, 0);
	};

	const handleSearch = (e) => {
		e.preventDefault();
		if (searchQuery.trim()) {
			navigate(
				`/diseases/${searchQuery.charAt(0).toUpperCase()}?search=${encodeURIComponent(searchQuery)}`,
			);
		} else {
			navigate("/diseases");
		}
		window.scrollTo(0, 0);
	};

	return (
		<>
			{/* Hero Carousel Section */}
			<section className="tgh-hero-carousel-section">
				<div
					id="tgh-hero-carousel"
					className="carousel slide carousel-fade"
					data-bs-ride="carousel"
					data-bs-interval="5000"
					data-bs-pause="hover">
					<div className="carousel-inner">
						{carouselItems.map((item, index) => (
							<div
								key={index}
								className={`carousel-item ${index === 0 ? "active" : ""}`}>
								<div className="tgh-carousel-image-wrapper">
									{item.type === "video" ? (
										<video
											className="d-block w-100"
											autoPlay
											muted
											playsInline
											poster={item.poster}>
											<source src={item.src} type="video/mp4" />
											Your browser does not support the video tag.
										</video>
									) : (
										<img
											src={item.image}
											className="d-block w-100"
											alt={item.title}
										/>
									)}
									<div className="tgh-carousel-overlay"></div>
								</div>
								<div className="tgh-carousel-content">
									<div className="container">
										<div className="row">
											<div className="col-lg-7">
												<h1 className="tgh-carousel-title">{item.title}</h1>
												<p className="tgh-carousel-subtitle">{item.subtitle}</p>
												<div className="tgh-carousel-actions">
													{(() => {
														const subtitleText =
															typeof item.subtitle === "string" ? item.subtitle : "";
														return (
													<Link to={item.link} className="tgh-carousel-link">
															{subtitleText.toLowerCase().includes("innovation")
															? "Learn how we drive innovation"
															: "Learn more"}{" "}
														<span>→</span>
													</Link>
														);
													})()}
													<Link
														to="/contact"
														className="btn btn-outline-light btn-lg">
														{item.buttonText}
													</Link>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
					<button
						className="carousel-control-prev"
						type="button"
						data-bs-target="#tgh-hero-carousel"
						data-bs-slide="prev">
						<span
							className="carousel-control-prev-icon"
							aria-hidden="true"></span>
						<span className="visually-hidden">Previous</span>
					</button>
					<button
						className="carousel-control-next"
						type="button"
						data-bs-target="#tgh-hero-carousel"
						data-bs-slide="next">
						<span
							className="carousel-control-next-icon"
							aria-hidden="true"></span>
						<span className="visually-hidden">Next</span>
					</button>
				</div>
			</section>

			{/* Disease Search Section */}
			<section className="tgh-disease-search py-5">
				<div className="container">
					<div className="row">
						<div className="col-lg-6">
							<h3 className="tgh-search-section-title mb-4">
								Find diseases & conditions by first letter
							</h3>
							<div className="tgh-alphabet-grid">
								{alphabet.map((letter) => (
									<button
										key={letter}
										type="button"
										className={`tgh-alphabet-btn ${selectedLetter === letter ? "active" : ""}`}
										onClick={() => handleLetterClick(letter)}>
										{letter}
									</button>
								))}
								{numbers.map((num) => (
									<button
										key={num}
										type="button"
										className={`tgh-alphabet-btn ${selectedLetter === num ? "active" : ""}`}
										onClick={() => handleLetterClick(num)}>
										{num}
									</button>
								))}
							</div>
							<div className="mt-4">
								<Link to="/diseases" className="btn btn-outline-primary">
									View All Diseases & Conditions{" "}
									<i className="bi bi-arrow-right ms-1"></i>
								</Link>
							</div>
						</div>
						<div className="col-lg-6">
							<h3 className="tgh-search-section-title mb-4">
								Search diseases & conditions
							</h3>
							<form onSubmit={handleSearch} className="tgh-search-form">
								<div className="input-group">
									<input
										type="text"
										className="form-control form-control-lg"
										placeholder="Search"
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
									/>
									<button className="btn btn-primary" type="submit">
										<i className="bi bi-search"></i>
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			</section>

			{/* Healing Starts Here Section */}
			<section className="tgh-hero-section">
				<div className="container">
					<div className="row align-items-center">
						<div className="col-lg-6">
							<h1 className="tgh-hero-main-title">Your Health, Our Priority</h1>
							<div className="tgh-hero-content">
								<h2 className="tgh-hero-subtitle">
									The right answers the first time
								</h2>
								<p className="tgh-hero-text">
								Effective healthcare begins with accurate diagnosis and timely treatment. Our experienced specialists, advanced diagnostic services, and patient-centered approach ensure that every patient receives the right care at the right time.
								</p>
								<div className="tgh-hero-highlight">
									<h3 className="tgh-hero-subtitle">
									A Leading Referral Hospital in Greater Accra
									</h3>
									<p className="tgh-hero-text">
									As one of the major secondary-level referral hospitals in Ghana, Tema General Hospital serves Tema and surrounding communities with comprehensive clinical, emergency, maternal, surgical, and specialist healthcare services.{" "}
										<Link to="/about-us/overview" className="tgh-link">
											Learn more about Tema General Hospital
										</Link>
									</p>
								</div>
								<div className="tgh-hero-actions mt-4">
									<Link
										to="/about-us/overview"
										className="btn btn-outline-primary btn-lg">
										Why choose Tema General Hospital
									</Link>
								</div>
							</div>
						</div>
						<div className="col-lg-6">
							<div className="tgh-hero-image">
								<img
									src="/img/carousel/nicu2.JPG"
									alt="Tema General Hospital"
									className="img-fluid"
								/>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Dedicated Healthcare Team Section */}
			<section className="tgh-world-class py-5">
				<div className="container">
					<div className="row align-items-center">
						<div className="col-lg-6">
							<div className="tgh-world-class-image">
								<img
									src="/img/carousel/theatre.JPG"
									alt="Dedicated Healthcare Team"
									className="img-fluid"
								/>
							</div>
						</div>
						<div className="col-lg-6">
							<h2 className="tgh-section-title">
							Quality Healthcare for Every Community
							</h2>
							<p className="tgh-section-text">
							We are committed to promoting health and improving lives through accessible, affordable, and patient-focused healthcare services. With skilled healthcare professionals, modern facilities, and continuous improvements in service delivery, Tema General Hospital remains dedicated to caring for our community with excellence.
							</p>
							<Link to="/services" className="btn btn-outline-primary">
								Our Services
							</Link>
						</div>
					</div>
				</div>
			</section>

			{/* yr Management Team Section - Card Layout */}
			<section className="tgh-senior-management-section py-5 bg-light">
				<div className="container">
					<h2 className="tgh-section-title mb-3">Senior Management Team</h2>
					<p className="text-muted mb-4">
						Meet our experienced leadership team dedicated to providing
						world-class healthcare.
					</p>

					{loadingManagement ? (
						<div className="text-center py-5">
							<div className="spinner-border text-primary" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
						</div>
					) : seniorManagement.length > 0 ? (
						<>
							{/* Swiper: auto-slide, 1 card on small (e.g. 6.5"), 2 on medium, 3 on large.
								 No manual duplication so order is always 1 → 2 → 3 → … → 1. */}
							<Swiper
								className="tgh-management-slider tgh-management-swiper"
								modules={[Autoplay, Pagination, Navigation]}
								spaceBetween={24}
								slidesPerView={3}
								slidesPerGroup={1}
								initialSlide={0}
								loop={false}
								rewind={true} // after last slide, go back to first in order
								autoplay={{
									delay: 4000,
									disableOnInteraction: false,
								}}
								pagination={{
									clickable: true,
									bulletClass: "swiper-pagination-bullet",
									bulletActiveClass: "swiper-pagination-bullet-active",
								}}
								navigation={true}
								speed={600}
								grabCursor={true}
								breakpoints={{
									0: { slidesPerView: 1, spaceBetween: 16 },
									768: { slidesPerView: 2, spaceBetween: 20 },
									992: { slidesPerView: 3, spaceBetween: 24 },
								}}>
								{seniorManagement.map((member) => (
									<SwiperSlide key={member.id}>
										<div
											className="card tgh-management-card h-100 shadow-sm border-0"
											style={{
												wordBreak: "normal",
												overflowWrap: "break-word",
											}}>
											<Link
												to={`/senior-management/${member.id}`}
												className="text-decoration-none">
												<div className="tgh-management-image-wrapper">
													<img
														src={
															member.image ||
															"/img/Male Professional Headshot - East African.jpeg"
														}
														alt={member.name || "Management member"}
														className="tgh-management-image"
														onError={(e) => {
															e.target.src =
																"/img/Male Professional Headshot - East African.jpeg";
														}}
													/>
												</div>
											</Link>
											<div
												className="card-body"
												style={{
													wordBreak: "normal",
													overflowWrap: "break-word",
													whiteSpace: "normal",
												}}>
												<h5
													className="tgh-management-name mb-2"
													style={{
														wordBreak: "normal",
														overflowWrap: "break-word",
													}}>
													<Link
														to={`/senior-management/${member.id}`}
														className="text-decoration-none">
														{member.name || "Name not available"}
													</Link>
												</h5>
												<p
													className="tgh-management-designation mb-0"
													style={{
														wordBreak: "normal",
														overflowWrap: "break-word",
													}}>
													{member.designation || "Designation not available"}
												</p>
											</div>
										</div>
									</SwiperSlide>
								))}
							</Swiper>

							<div className="text-center mt-4">
								<Link
									to="/senior-management"
									className="btn btn-primary btn-lg">
									View More <i className="bi bi-arrow-right ms-2"></i>
								</Link>
							</div>
						</>
					) : (
						<div className="text-center py-5">
							<p className="text-muted">
								No management team members available at this time.
							</p>
						</div>
					)}
				</div>
			</section>

			{/* Latest News Section */}
			<section className="tgh-latest-news py-5">
				<div className="container">
					<div className="row mb-4">
						<div className="col-lg-8">
							<h2 className="tgh-section-title mb-2">Latest News</h2>
							<p className="text-muted mb-0">Be the first to read</p>
						</div>
						<div className="col-lg-4 text-lg-end">
							<Link to="/news" className="btn btn-outline-primary">
								View All News <i className="bi bi-arrow-right ms-1"></i>
							</Link>
						</div>
					</div>
					{loadingNews ? (
						<div className="text-center py-5">
							<div className="spinner-border text-primary" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
						</div>
					) : latestNews.length > 0 ? (
						<div className="row g-4 tgh-card-grid">
							{latestNews.map((news) => (
								<div key={news.id} className="col-xl-4 col-lg-6 col-md-6 col-12">
									<div
										className="card border-0 shadow-sm h-100"
										style={{ borderRadius: "8px", overflow: "hidden" }}>
										<Link
											to={`/news/${news.id}`}
											className="text-decoration-none">
											<div className="tgh-card-media">
												<img
													src={
														news.image
															? `/uploads/${news.image}`
															: "/img/News.jpeg"
													}
													className="card-img-top"
													alt={news.title}
													onError={(e) => {
														e.target.src = "/img/News.jpeg";
													}}
												/>
											</div>
										</Link>
										<div className="card-body p-4">
											<h5
												className="card-title mb-3"
												style={{
													fontSize: "1.1rem",
													fontWeight: "600",
													lineHeight: "1.4",
												}}>
												<Link
													to={`/news/${news.id}`}
													className="text-decoration-none"
													style={{ color: "#1a1a1a" }}>
													{news.title}
												</Link>
											</h5>
											{news.content && (
												<p
													className="card-text text-muted mb-3"
													style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
													{news.content.length > 120
														? `${news.content.substring(0, 120)}...`
														: news.content}
												</p>
											)}
											<div className="d-flex justify-content-between align-items-center">
												<small
													className="text-muted"
													style={{ fontSize: "0.85rem" }}>
													{new Date(news.date).toLocaleDateString("en-US", {
														year: "numeric",
														month: "long",
														day: "numeric",
													})}
												</small>
												{news.full_name && (
													<small
														className="text-muted"
														style={{ fontSize: "0.85rem" }}>
														By {news.full_name || news.username || "Admin"}
													</small>
												)}
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="text-center py-5">
							<p className="text-muted">No news available at this time.</p>
						</div>
					)}
				</div>
			</section>

			{/* Locations Section */}
			<section className="tgh-locations-section py-5">
				<div className="container">
					<div className="row mb-4">
						<div className="col-lg-6">
							<h2 className="tgh-section-title">Locations</h2>
							<p className="tgh-section-text">
								Learn more about Tema General Hospital location or get
								directions.
							</p>
							<a
								href="https://maps.app.goo.gl/fYCxAzFS3JNGUKnNA"
								target="_blank"
								rel="noopener noreferrer"
								className="btn btn-outline-primary">
								Explore location
							</a>
						</div>
					</div>
					<div className="row g-4">
						<div className="col-lg-4 col-md-6">
							<div className="tgh-location-card">
								<div className="tgh-location-image">
									<img
										src="/img/carousel/admin2.jpg"
										alt="Tema General Hospital"
										className="img-fluid"
									/>
								</div>
								<div className="tgh-location-content">
									<h3 className="tgh-location-title">
										<Link to="/contact">Tema General Hospital</Link>
									</h3>
									<p className="tgh-location-subtitle">
										Tema Community 12, Tema, Ghana
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Donors & Partners Section */}
			<section className="tgh-donors-partners bg-light py-5">
				<div className="container">
					<div className="tgh-donors-partners-header text-center mb-4 mb-md-5">
						<h2 className="tgh-section-title mb-2">Donors & Partners</h2>
						<p className="text-muted mb-0">
							We are grateful to the organizations supporting our mission.
						</p>
					</div>
					{loadingPartners ? (
						<div className="text-center py-5">
							<div className="spinner-border text-primary" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
						</div>
					) : donorsPartners.length > 0 ? (
						<>
							{/* Infinite marquee on all viewports (avoids react-slick blank / breakpoint mismatch on mobile) */}
							<div
								className="tgh-partners-marquee"
								aria-label="Donors and partners logos">
								<div
									className="tgh-partners-track"
									style={{
										// Drives CSS keyframes for seamless looping (measured in px)
										["--tgh-partners-shift"]: `${marqueeShiftPx}px`,
									}}>
									<div className="tgh-partners-segment" ref={marqueeSegmentRef}>
										{donorsPartners.map((partner) => (
											<Link
												key={partner.id}
												to={`/donors-partners/${partner.id}`}
												className="tgh-partner-slide-link tgh-partner-marquee-item text-decoration-none"
												aria-label={`View ${partner.name || "partner"} details`}>
												<div className="tgh-partner-card">
													<div className="tgh-partner-logo-wrap">
														<img
															src={partner.image}
															alt={partner.name || "Partner logo"}
															className="img-fluid tgh-partner-logo"
															decoding="async"
															loading="eager"
															fetchpriority="high"
															onError={(e) => {
																e.target.src = "/img/Insurance.jpeg";
															}}
														/>
													</div>
												</div>
											</Link>
										))}
									</div>
									<div className="tgh-partners-segment">
										{donorsPartners.map((partner) => (
											<Link
												key={`${partner.id}-dup`}
												to={`/donors-partners/${partner.id}`}
												className="tgh-partner-slide-link tgh-partner-marquee-item text-decoration-none"
												aria-label={`View ${partner.name || "partner"} details`}>
												<div className="tgh-partner-card">
													<div className="tgh-partner-logo-wrap">
														<img
															src={partner.image}
															alt={partner.name || "Partner logo"}
															className="img-fluid tgh-partner-logo"
															decoding="async"
															loading="eager"
															fetchpriority="high"
															onError={(e) => {
																e.target.src = "/img/Insurance.jpeg";
															}}
														/>
													</div>
												</div>
											</Link>
										))}
									</div>
								</div>
							</div>
							<div className="text-center mt-4">
								<Link to="/donors-partners" className="btn btn-primary btn-lg">
									View More <i className="bi bi-arrow-right ms-2"></i>
								</Link>
							</div>
						</>
					) : (
						<div className="text-center py-5">
							<p className="text-muted">
								No donors/partners available at this time.
							</p>
						</div>
					)}
				</div>
			</section>

			{/* Footer CTA Section */}
			<section className="tgh-footer-cta py-5 bg-primary text-white">
				<div className="container">
					<div className="row align-items-center">
						<div className="col-lg-8">
							<h2 className="text-white mb-3">Ready to get started?</h2>
							<p className="text-white-50 mb-0">
								Contact us or learn more about our services.
							</p>
						</div>
						<div className="col-lg-4 text-lg-end">
							<Link to="/contact" className="btn btn-light btn-lg">
								Contact us
							</Link>
						</div>
					</div>
				</div>
			</section>

			{/* Event Notification Popup */}
			<EventNotification />
		</>
	);
}

export default Home;
